/**
 * Cart line display for IKEA multi-pack (s-prefixed) items: derive package breakdown
 * from `dimensions` (one row per PDP measurement group) instead of aggregated `packageLines.qty`.
 */

function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

function normalizeSkuDigits(sku) {
  return String(sku || '').replace(/\D/g, '');
}

export function findPackageLineForSku(packageLines, sku) {
  const target = normalizeSkuDigits(sku);
  if (!target || !Array.isArray(packageLines)) return null;
  return (
    packageLines.find((pl) => normalizeSkuDigits(pl.sku) === target) ||
    packageLines.find((pl) => {
      const d = normalizeSkuDigits(pl.sku);
      return d && (target.endsWith(d) || d.endsWith(target));
    }) ||
    null
  );
}

export function isIkeaMultipackCartItem(item) {
  return (
    /^s/i.test(String(item?.id || '').trim()) &&
    Array.isArray(item?.dimensions) &&
    item.dimensions.some((d) => Array.isArray(d.packages) && d.packages.length > 0)
  );
}

/**
 * One entry per row in dimensions[].packages (matches fulfillment / PDP structure).
 * `colli` is IKEA quantity for that size line (identical cartons).
 */
export function getMultipackPhysicalLines(item) {
  const lines = [];
  if (!Array.isArray(item?.dimensions)) return lines;
  let ordinal = 0;
  for (const dim of item.dimensions) {
    const pkgs = Array.isArray(dim.packages) ? dim.packages : [];
    for (const p of pkgs) {
      const colli = Math.max(1, Number(p.packages != null ? p.packages : 1) || 1);
      const sku = p.articleNumber || dim.sku;
      if (!sku) continue;
      ordinal += 1;
      lines.push({
        ordinal,
        sku: String(sku).trim(),
        colli,
      });
    }
  }
  return lines;
}

/**
 * IKEA catalog reference for one component block (dimension group): unit price × ordered bundle qty.
 * Used so we do not repeat full catalog € on every physical carton row.
 *
 * @returns {{ unit: number, bundleAmt: number, total: number } | null}
 */
export function getIkeaCatalogRetailSummaryForComponent(item, dimension) {
  const bundleAmt = Math.max(1, Number(item.amount ?? item.quantity ?? 1) || 1);
  const refSku =
    Array.isArray(dimension?.packages) && dimension.packages.length > 0
      ? dimension.packages[0].articleNumber || dimension.sku
      : dimension?.sku;
  if (!refSku) return null;
  const pl = findPackageLineForSku(item.packageLines, refSku);
  const raw = pl?.catalogUnitPrice;
  if (raw == null || !Number.isFinite(Number(raw))) return null;
  const unit = Number(raw);
  return { unit, bundleAmt, total: round2(unit * bundleAmt) };
}

/** @param {{ unit: number, bundleAmt: number, total: number }} display */
export function formatIkeaCatalogRetailSummary(display) {
  if (!display || display.unit == null || !Number.isFinite(Number(display.unit))) return '';
  const unit = Number(display.unit);
  const bundleAmt = Math.max(1, Number(display.bundleAmt || 1));
  const total =
    display.total != null && Number.isFinite(Number(display.total))
      ? Number(display.total)
      : round2(unit * bundleAmt);
  const u = unit.toFixed(2);
  const t = total.toFixed(2);
  if (bundleAmt > 1) return `IKEA €${u} × ${bundleAmt} = €${t}`;
  return `IKEA €${u}`;
}

/** @returns {{ lines: Array<{ ordinal: number, sku: string, colli: number, name: string | null, ikeaCatalogDisplay: { unit: number, bundleAmt: number, total: number } | null }>, packageCount: number }} */
export function getMultipackDisplayRows(item) {
  const physical = getMultipackPhysicalLines(item);
  const bundleAmt = Math.max(1, Number(item.amount ?? item.quantity ?? 1) || 1);
  const seenSku = new Set();
  const rows = physical.map((line) => {
    const pl = findPackageLineForSku(item.packageLines, line.sku);
    const name = pl?.name || null;
    const digits = normalizeSkuDigits(line.sku);
    const unit =
      pl?.catalogUnitPrice != null && Number.isFinite(Number(pl.catalogUnitPrice))
        ? Number(pl.catalogUnitPrice)
        : null;
    let ikeaCatalogDisplay = null;
    if (unit != null && digits && !seenSku.has(digits)) {
      seenSku.add(digits);
      ikeaCatalogDisplay = {
        unit,
        bundleAmt,
        total: round2(unit * bundleAmt),
      };
    }
    return { ...line, name, ikeaCatalogDisplay };
  });
  return {
    lines: rows,
    packageCount: physical.length,
  };
}
